import { Skeleton } from 'antd'
import { saveAs } from 'file-saver'
import { search } from 'jmespath'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { PermissionButton, useModal, useToolkitsStore } from 'react-toolkits'
import type { ButtonSchema } from '../schemas'
import { buttonSchema } from '../schemas'
import { getStoreData, setStoreData, useDataStore } from '../stores'
import { renderSchemaArray, replaceValueWithJMESPath } from '../utils'
import { withParsedData } from './base'

const ModalButtonRenderer = <S extends object>({ data, source }: { data: ButtonSchema; source?: S }) => {
  const { text, buttonType, modal, storeIdRef, storeId, method, url, params, body, ...rest } = data
  const { axios } = useToolkitsStore(s => s)

  const [content, setContent] = useState<ReactNode | null>(null)

  const { show, modal: modalElement } = useModal({
    title: modal?.title,
    content,
    width: modal?.width,
    destroyOnClose: true,
    afterOpen: async () => {
      setContent(<Skeleton />)

      let _content

      if (url) {
        const response = await axios.request({
          method,
          url,
          params: params && replaceValueWithJMESPath(getStoreData(storeIdRef), params),
          data: body && replaceValueWithJMESPath(getStoreData(storeIdRef), body),
        })

        if (storeId) {
          setStoreData(storeId, response.data.data)
        }

        _content = renderSchemaArray(modal?.body, response.data.data)
      } else {
        _content = renderSchemaArray(modal?.body, source)
      }
      setContent(_content)
    },
  })

  return (
    <>
      <PermissionButton {...rest} type={buttonType} onClick={show}>
        {text}
      </PermissionButton>
      {createPortal(modalElement, document.body)}
    </>
  )
}

const DownloadButtonRenderer = ({ data }: { data: ButtonSchema }) => {
  const { text, buttonType, url, method, params, body, storeIdRef, action, ...rest } = data
  const { dataMap } = useDataStore()
  const { axios } = useToolkitsStore(s => s)

  const onClick = async () => {
    const storeData = storeIdRef ? dataMap.get(storeIdRef) : null
    const newUrl = url!.replace(/\$\{([^}]+)\}/g, (_match, key) => search(storeData, key) ?? '')

    const response = await axios({
      url: newUrl,
      method,
      params,
      data: body,
      responseType: 'blob',
    })

    const header = response.headers?.['content-disposition']
    let filename = 'unknown'
    filename = header?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]
    const blob = new Blob([response.data], { type: 'charset=utf-8' })
    saveAs(blob, filename)
  }

  return (
    <PermissionButton {...rest} type={buttonType} onClick={onClick}>
      {text}
    </PermissionButton>
  )
}

export const ButtonRenderer = withParsedData(buttonSchema)<{ source?: object }>(({ parsedData, source }) => {
  const { action, code, text, buttonType, params, body, storeIdRef, showLoading } = parsedData
  const { dataMap } = useDataStore()
  const storeData = storeIdRef ? dataMap.get(storeIdRef) : null
  const newParams = params && replaceValueWithJMESPath(storeData, params)
  const newBody = body && replaceValueWithJMESPath(storeData, body)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newData = { ...parsedData, params: newParams, body: newBody } as any

  if (action === 'modal') {
    return <ModalButtonRenderer data={newData} source={source} />
  } else if (action === 'download') {
    return <DownloadButtonRenderer data={newData} />
  }

  return (
    <PermissionButton code={code} type={buttonType} showLoading={showLoading}>
      {text}
    </PermissionButton>
  )
})
